import { Formik } from 'formik';
import * as Yup from 'yup';

import * as Styled from './styles';

import {
  Button,
  IndicatorOption,
  InputField,
  MaskInputField,
  SingleSelectField,
  toastify,
} from '#ui-kit';

import { FormContainer, FormSection } from '#components';

import {
  formatPhoneNumber,
  getChangedFields,
  RESTAURANT_STATUS_OPTIONS,
} from '#utils';
import { RestaurantsService } from '#services/restaurants';
import { OptionProps } from 'components/ui-kit/Inputs/types';
import { RestaurantResponse, RestaurantStatusEnum } from 'types';

const validationSchema = Yup.object().shape({
  code: Yup.string().nullable(),
  status: Yup.mixed<RestaurantStatusEnum>().oneOf(
    Object.values(RestaurantStatusEnum) as RestaurantStatusEnum[],
  ),
});

type FormProps = { data: RestaurantResponse };

const Form: React.FC<FormProps> = ({ data }) => {
  const restaurantsService = new RestaurantsService();

  const initialValues = {
    address: data.address,
    city: data.city,
    id: data.id,
    inn: String(data.tin ?? ''),
    name: data.name,
    phoneNumber: data.phoneNumber ? formatPhoneNumber(data.phoneNumber) : '',
    code: data.code ?? '',
    status: data.status,
  };

  const statusOptions: OptionProps<string>[] = RESTAURANT_STATUS_OPTIONS;

  const onSubmit = async (values: typeof initialValues) => {
    const changed = getChangedFields(initialValues, values);

    if (Object.keys(changed).length === 0) {
      return;
    }

    try {
      await restaurantsService.updateRestaurant(data.id, changed);

      toastify.success('Данные ресторана успешно обновлены');
    } catch {
      toastify.error('Не удалось обновить данные ресторана');
    }
  };

  return (
    <Styled.FormikContainer>
      <Formik {...{ initialValues, onSubmit, validationSchema }}>
        {({
          handleSubmit,
          values,
          initialValues: formInitialValues,
          isSubmitting,
        }) => {
          const changed = getChangedFields(formInitialValues, values);
          const hasAllowedChanges =
            Object.prototype.hasOwnProperty.call(changed, 'code') ||
            Object.prototype.hasOwnProperty.call(changed, 'status');

          return (
            <form onSubmit={handleSubmit} noValidate>
              <FormContainer
                leftSideActions={
                  <Button
                    className="create-restaurant__submit"
                    label="Сохранить"
                    type="button"
                    onClick={handleSubmit as unknown as () => void}
                    disabled={!hasAllowedChanges || isSubmitting}
                  />
                }
              >
                <FormSection label="Наименование" isRequired>
                  <InputField
                    disabled={true}
                    name="name"
                    placeholder="Введите наименование"
                  />
                </FormSection>
                <FormSection label="Адрес" isRequired>
                  <InputField
                    name="address"
                    placeholder="Введите адрес"
                    disabled
                  />
                </FormSection>
                <FormSection label="Номер телефона">
                  <MaskInputField
                    name="phoneNumber"
                    placeholder="Введите номер телефона"
                    mask="+7 (999) 999-99-99"
                    disabled
                  />
                </FormSection>
                <FormSection label="ИНН" isRequired>
                  <MaskInputField
                    name="inn"
                    placeholder="Введите ИНН"
                    mask="999999999"
                    disabled
                  />
                </FormSection>
                <FormSection label="Код">
                  <InputField name="code" placeholder="Введите код" />
                </FormSection>
                <FormSection label="Статус" isRequired>
                  <SingleSelectField
                    name="status"
                    options={statusOptions}
                    components={{ Option: IndicatorOption }}
                  />
                </FormSection>
              </FormContainer>
            </form>
          );
        }}
      </Formik>
    </Styled.FormikContainer>
  );
};

export default Form;
